import React, { useMemo } from "react";
import { Checkbox, Input, Select } from "antd";
import { Icon } from "Components";

interface RoleOption {
    key: string;
    label: string;
    count: number;
    description: string;
}

interface PropertyRole {
    key: string;
    count: number;
}

interface Props {
    propertyOptions: any[];
    roleOptions: RoleOption[];
    propertyRoles: Record<string, PropertyRole[]>;

    selectedRoles: string[];
    setSelectedRoles: (roles: string[]) => void;

    selectedProperty?: string;
    setSelectedProperty: (value?: string) => void;

    roleSearchQuery: string;
    setRoleSearchQuery: (value: string) => void;
}

const RoleRecipients: React.FC<Props> = ({
    propertyOptions,
    roleOptions,
    propertyRoles,
    selectedRoles,
    setSelectedRoles,
    selectedProperty,
    setSelectedProperty,
    roleSearchQuery,
    setRoleSearchQuery,
}) => {

    const allPropertyRoles = useMemo(() => roleOptions.map((role) => {
        const totalCount = Object.entries(propertyRoles)
            .filter(([key]) => key.toLowerCase() !== "all")
            .reduce((acc, [_, roles]) => {
                const match = roles.find((r) => r.key === role.key);
                return acc + (match?.count ?? 0);
            }, 0);
        return { ...role, count: totalCount };
    }), [roleOptions, propertyRoles]);

    const availableRoles = useMemo(() => !selectedProperty
        ? []
        : selectedProperty.toLowerCase() === "all"
            ? allPropertyRoles
            : (propertyRoles[selectedProperty] ?? []).map(({ key, count }: PropertyRole) => {
                const base = roleOptions.find((r) => r.key === key)!;
                return { ...base, count };
            }), [selectedProperty, allPropertyRoles]);

    const allRolesSelected =
        availableRoles.length > 0 && selectedRoles.length === availableRoles.length;

    const handleSelectAll = () => {
        if (allRolesSelected) {
            setSelectedRoles([]);
        } else {
            setSelectedRoles(availableRoles.map((r) => r.key));
        }
    };

    const handleRoleToggle = (key: string) => {
        if (selectedRoles.includes(key)) {
            setSelectedRoles(selectedRoles.filter((r) => r !== key));
        } else {
            setSelectedRoles([...selectedRoles, key]);
        }
    };

    const totalRoleCount = availableRoles
        .filter((r) => selectedRoles.includes(r.key))
        .reduce((acc, r) => acc + r.count, 0);

    return (
        <div className="phishing-step3__role-section">

            <div className="phishing-step3__role-filters">
                <div className="phishing-step3__filter-group">
                    <div className="phishing-step3__label">
                        Select property <span className="required">*</span>
                    </div>

                    <Select
                        placeholder="Select option"
                        className="campaign-setup-select"
                        dropdownClassName="campaign-setup-filter-option-select"
                        style={{ width: "100%" }}
                        value={selectedProperty}
                        onChange={(val) => {
                            setSelectedProperty(val);
                            setSelectedRoles([]);
                        }}
                        options={propertyOptions}
                        size="large"
                        getPopupContainer={(trigger) => trigger.parentElement}
                    />
                </div>

                <div className="phishing-step3__filter-group phishing-step3__role-search">
                    <div>
                        <Input.Search
                            placeholder="Search by role name"
                            value={roleSearchQuery}
                            onChange={(e: any) => setRoleSearchQuery(e.target.value)}
                            suffix={<Icon type="search" />}
                        />
                    </div>
                </div>

            </div>

            <div className="phishing-step3__role-list-header">
                <div className="phishing-step3__label">
                    Select a role
                    {totalRoleCount > 0 && (
                        <>
                            <span className="phishing-step3__dot"> · </span>
                            <span className="phishing-step3__total-count">
                                {totalRoleCount} users selected <span className="required">*</span>
                            </span>
                        </>
                    )}
                </div>

                <button
                    className="phishing-step3__select-all"
                    onClick={handleSelectAll}
                >
                    {allRolesSelected ? "Deselect all" : "Select all"}
                </button>
            </div>

            <div className="phishing-step3__role-list">
                {availableRoles
                    .filter(
                        (r) =>
                            r.label.toLowerCase().includes(roleSearchQuery.toLowerCase()) ||
                            r.description
                                .toLowerCase()
                                .includes(roleSearchQuery.toLowerCase())
                    )
                    .map((role) => (
                        <div
                            key={role.key}
                            className="phishing-step3__role-item"
                            onClick={() => handleRoleToggle(role.key)}
                        >
                            <Checkbox
                                checked={selectedRoles.includes(role.key)}
                                onChange={() => handleRoleToggle(role.key)}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <div className="phishing-step3__role-info">
                                <div className="phishing-step3__role-title">
                                    <Icon type="recipientRole" />
                                    {role.label}
                                </div>
                                <span className="phishing-step3__role-desc">
                                    {role.description} - ({role.count}) users
                                </span>
                            </div>
                        </div>
                    ))}
            </div>
        </div>
    );
};

export default RoleRecipients;